import { showToast } from './toast'
import { logger } from './logger'

const DEFAULT_ERROR_MESSAGE = 'Something went wrong'

function readMessageField(source: Record<string, unknown>): string | string[] | undefined {
  const m = source.message

  if (typeof m === 'string' || Array.isArray(m)) {
    return m as string | string[]
  }

  return undefined
}

/**
 * Normalizes thrown `callApi` errors (and similar shapes) into user-facing strings.
 * Nest validation errors use `payload.message: string[]`.
 */
export function extractApiErrorMessages(err: unknown): string[] {
  if (err === null || err === undefined) {
    return [DEFAULT_ERROR_MESSAGE]
  }

  if (typeof err === 'string' && err.trim()) {
    return [err.trim()]
  }

  if (typeof err !== 'object') {
    return [DEFAULT_ERROR_MESSAGE]
  }

  const obj = err as Record<string, unknown>
  let msg = readMessageField(obj)
  const innerPayload = obj.payload

  if ((msg === null || msg === undefined) && innerPayload && typeof innerPayload === 'object') {
    msg = readMessageField(innerPayload as Record<string, unknown>)
  }

  if (Array.isArray(msg)) {
    const lines = msg
      .filter((m): m is string => typeof m === 'string')
      .map((m) => m.trim())
      .filter((m) => m.length > 0)

    if (lines.length > 0) {
      return lines
    }
  }

  if (typeof msg === 'string' && msg.trim()) {
    return [msg.trim()]
  }

  return [DEFAULT_ERROR_MESSAGE]
}

export function formatApiErrorFromThrown(err: unknown): string {
  return formatApiErrorMessage(extractApiErrorMessages(err))
}

export function showApiErrorToasts(err: unknown): void {
  extractApiErrorMessages(err).forEach((text) => {
    showToast(text, {
      variant: 'error',
      autoHideDuration: 9000,
    })
  })
}

export const formatApiErrorMessage = (message: string | string[] | undefined): string => {
  if (!message) {
    return DEFAULT_ERROR_MESSAGE
  }

  if (typeof message === 'string') {
    return message || DEFAULT_ERROR_MESSAGE
  }

  const formattedMessages = message.filter((msg): msg is string => typeof msg === 'string')

  return formattedMessages.length > 0 ? formattedMessages.join(', ') : DEFAULT_ERROR_MESSAGE
}

export const handleApiError = (err: any, context?: string) => {
  const errorMessage = err?.payload?.message || err?.message || 'Unknown error'
  const status = err?.status || err?.statusCode

  logger.error(context ? `${context}: ${errorMessage}` : `API Error: ${errorMessage}`, err, {
    status,
    payload: err?.payload,
    context,
  })

  if (typeof err === 'object') {
    err.slice(0, 3).forEach((item: any) => {
      const message = typeof item === 'object' ? 'Something went wrong' : item

      showToast(message, {
        variant: 'error',
        autoHideDuration: 6000,
      })
    })
  } else {
    showToast(err, {
      variant: 'error',
      autoHideDuration: 6000,
    })
  }
}
